import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShoppingBag,
  Plus,
  Trash,
  Pulse,
  CircleNotch,
  CloudArrowUp,
  CheckSquare,
  Square,
  CaretDown,
  Palette,
  Warning,
  Question,
  ArrowRight,
  Package,
} from "@phosphor-icons/react";
import { t } from "../i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import ApplyLogPanel from "./ApplyLogPanel";
import ShopifyHealthBanner from "./ShopifyHealthBanner";
import ProductPickerModal from "./ProductPickerModal";
import ConditionAuditPanel from "./ConditionAuditPanel";
import {
  listShopifyConnections,  createShopifyConnection,
  detectShopifyLanguage,
  testShopifyConnection,
  deleteShopifyConnection,
  shopifyScan,
  shopifyScanWithProgress,
  shopifyApply,
  shopifyOAuthStatus,
  shopifyOAuthInstallUrl,
  shopifyThemeScan,
  shopifyThemeApply,
} from "../lib/api";

const RESOURCE_TYPES = [
  "product",
  "page",
  "custom_collection",
  "smart_collection",
  "policy",
];

const FIELD_KEYS = ["title", "body_html", "seo_title", "seo_description"];
const FIELD_LABEL = {
  title: "shopify_fields_title",
  body_html: "shopify_fields_body",
  seo_title: "shopify_fields_seo_title",
  seo_description: "shopify_fields_seo_desc",
};

function HelpBlock({ lang, titleKey, steps, troubleshootKey, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-blue-400 bg-blue-50" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left ma-trans hover:bg-blue-100"
        data-testid={`${testid}-toggle`}
      >
        <Question size={14} weight="fill" className="text-blue-700" />
        <span className="text-[11px] font-mono uppercase tracking-widest text-blue-900">
          {t(lang, titleKey)}
        </span>
        <CaretDown
          size={12}
          weight="bold"
          className={`ml-auto text-blue-700 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-4 py-3 border-t border-blue-300 bg-white space-y-2">
          <ol className="list-decimal list-inside space-y-2 text-[12px] leading-relaxed text-neutral-800">
            {steps.map((k) => (
              <li key={k} data-testid={`${testid}-${k}`}>{t(lang, k)}</li>
            ))}
          </ol>
          {troubleshootKey && (
            <div className="mt-3 border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 leading-relaxed">
              <strong className="font-mono uppercase tracking-widest text-[10px] mr-2">!</strong>
              {t(lang, troubleshootKey)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function scoreColor(score) {
  if (score >= 85) return "text-emerald-700 border-emerald-600 bg-emerald-50";
  if (score >= 70) return "text-amber-700 border-amber-600 bg-amber-50";
  if (score >= 50) return "text-orange-700 border-orange-600 bg-orange-50";
  return "text-red-700 border-red-600 bg-red-50";
}

function getApplyKey(r) {
  return `${r.resource_type}:${r.resource_id}`;
}

// Iter 39 — multilingual regex matching the "Mixed languages" rule label
// emitted by build_shopify_field_system_prompt across FR/EN/ES/IT/PT/DE.
const MIXED_LANG_RX = /mixed\s*languag|mezcla\s*de\s*idiomas|langues?\s*mixtes?|lingue\s*miste|idiomas?\s*mistos|sprachmischung|sprachen\s*gemischt/i;

function hasMixedLang(r) {
  if (!r?.audit) return false;
  for (const fk of FIELD_KEYS) {
    const issues = r.audit?.[fk]?.issues || [];
    for (const msg of issues) {
      if (typeof msg === "string" && MIXED_LANG_RX.test(msg)) return true;
    }
  }
  return false;
}

export default function ShopifyPanel({ lang }) {
  const [conns, setConns] = useState([]);
  const [loadingConns, setLoadingConns] = useState(false);
  const [form, setForm] = useState({ name: "", store_url: "", access_token: "" });
  const [connecting, setConnecting] = useState(false);
  const [activeConn, setActiveConn] = useState(null);
  const [selectedResources, setSelectedResources] = useState(["product"]);
  const [maxPer, setMaxPer] = useState(10);
  const [shopifySpeed, setShopifySpeed] = useState("fast");
  const [storeLang, setStoreLang] = useState(""); // "" = auto (preserve original) | fr/en/es/it/pt/de = force rewrites in this language
  const [storeLangDetection, setStoreLangDetection] = useState(null); // {detected, source, confidence}
  const [storeLangManuallyChanged, setStoreLangManuallyChanged] = useState(false);
  const [scoreThreshold, setScoreThreshold] = useState(70); // Auto-pilot: only fix resources with score <= this
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const [scanProgress, setScanProgress] = useState(null); // {done, total}
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedProductIds, setPickedProductIds] = useState([]); // [] = scan all (default)
  const [themeScanning, setThemeScanning] = useState(false);
  const [themeScan, setThemeScan] = useState(null);
  const [themeApplying, setThemeApplying] = useState(false);
  const [themeApplied, setThemeApplied] = useState({}); // {path: true}
  const [themeSeverity, setThemeSeverity] = useState("warning"); // info=all, warning=skip info, critical=only critical
  const [applying, setApplying] = useState(false);
  const [mixedLangOnly, setMixedLangOnly] = useState(false); // Iter 39 — filter scan results to mixed-language only
  // selections: { "product:12345": { title: true, body_html: false, ... } }
  const [selections, setSelections] = useState({});
  const [oauthConfigured, setOauthConfigured] = useState(false);
  const [oauthShop, setOauthShop] = useState("");

  const reload = useCallback(async () => {
    setLoadingConns(true);
    try {
      const list = await listShopifyConnections();
      setConns(list);
      if (!activeConn && list.length) setActiveConn(list[0].id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoadingConns(false);
    }
  }, [activeConn]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    shopifyOAuthStatus()
      .then((s) => setOauthConfigured(!!s.configured))
      .catch(() => setOauthConfigured(false));
  }, []);

  // Iter 37 — auto-detect the store's primary language each time the active
  // connection changes. Pre-fills the storeLang selector unless the user
  // has manually overridden it. Confidence shown next to the dropdown.
  useEffect(() => {
    if (!activeConn) {
      setStoreLangDetection(null);
      return;
    }
    let aborted = false;
    detectShopifyLanguage(activeConn)
      .then((res) => {
        if (aborted) return;
        setStoreLangDetection(res);
        // Only auto-fill if user hasn't manually picked a language for this session
        if (!storeLangManuallyChanged && res?.detected) {
          setStoreLang(res.detected);
        }
      })
      .catch(() => {
        if (!aborted) setStoreLangDetection(null);
      });
    return () => { aborted = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConn]);

  const startOAuth = () => {
    const shop = oauthShop.trim().toLowerCase();
    if (!shop.endsWith(".myshopify.com")) {
      toast.error("shop must end with .myshopify.com");
      return;
    }
    window.location.href = shopifyOAuthInstallUrl(shop);
  };

  const addConnection = async (e) => {
    e.preventDefault();
    if (connecting) return;
    setConnecting(true);
    try {
      const c = await createShopifyConnection(form);
      toast.success(t(lang, "shopify_connected"));
      setForm({ name: "", store_url: "", access_token: "" });
      setActiveConn(c.id);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setConnecting(false);
    }
  };

  const testConn = async (id) => {
    try {
      await testShopifyConnection(id);
      toast.success(t(lang, "shopify_test_ok"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const removeConn = async (id) => {
    try {
      await deleteShopifyConnection(id);
      if (activeConn === id) setActiveConn(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const toggleResource = (r) => {
    setSelectedResources((prev) =>
      prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]
    );
  };

  const runScan = async () => {
    if (!activeConn || scanning) return;
    setScanning(true);
    setScan(null);
    setSelections({});
    setScanProgress({ done: 0, total: 0 });
    try {
      const res = await shopifyScanWithProgress(
        {
          connection_id: activeConn,
          language: lang,
          resources: selectedResources.length ? selectedResources : ["product"],
          max_per_resource: Math.max(1, Math.min(500, parseInt(maxPer, 10) || 10)),
          speed: shopifySpeed,
          product_ids: pickedProductIds.length ? pickedProductIds : undefined,
          store_primary_language: storeLang || undefined,
        },
        (s) => setScanProgress({ done: s.done || 0, total: s.total || 0 }),
      );
      setScan(res);
      // pre-select all non-compliant fields
      const pre = {};
      (res.resources || []).forEach((r) => {
        if (r.readonly) return;
        const f = {};
        FIELD_KEYS.forEach((fk) => {
          const fa = r.audit?.[fk];
          if (fa && !fa.compliant && fa.suggested) f[fk] = true;
        });
        if (Object.keys(f).length) pre[getApplyKey(r)] = f;
      });
      setSelections(pre);
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "Error");
    } finally {
      setScanning(false);
      setScanProgress(null);
    }
  };

  const runThemeScan = async () => {
    if (!activeConn || themeScanning) return;
    setThemeScanning(true);
    setThemeScan(null);
    setThemeApplied({});
    try {
      const res = await shopifyThemeScan({
        connection_id: activeConn,
        language: lang,
        speed: shopifySpeed,
        store_primary_language: storeLang || undefined,
      });
      setThemeScan(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setThemeScanning(false);
    }
  };

  const SEV_RANK = { critical: 3, warning: 2, info: 1 };

  const runThemeAutoPilot = async () => {
    if (!themeScan || themeApplying) return;
    const minRank = SEV_RANK[themeSeverity] || 1;
    const filtered = (themeScan.findings || []).filter(
      (f) => (SEV_RANK[f.severity] || 1) >= minRank && !themeApplied[f.path],
    );
    if (filtered.length === 0) {
      toast.info(t(lang, "shopify_theme_autopilot_nothing"));
      return;
    }
    const total = themeScan.findings?.length || 0;
    const msg = t(lang, "shopify_theme_autopilot_confirm")
      .replace("{count}", String(filtered.length))
      .replace("{total}", String(total))
      .replace("{sev}", themeSeverity);
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    applyThemeFindings(filtered);
  };

  const applyThemeFindings = async (findings) => {
    if (!activeConn || themeApplying) return;
    const items = findings
      .filter((f) => !themeApplied[f.path])
      .map((f) => ({
        path: f.path,
        suggested_text: f.suggested_text,
        original_text: f.original_text,
      }));
    if (items.length === 0) {
      toast.error(t(lang, "apply_all_no_selection"));
      return;
    }
    setThemeApplying(true);
    try {
      const res = await shopifyThemeApply({ connection_id: activeConn, items });
      if (res.error) {
        toast.error(res.error);
      } else if ((res.updated_paths || []).length === 0) {
        toast.error(t(lang, "shopify_theme_no_apply"));
      } else {
        toast.success(`${t(lang, "shopify_theme_apply_done")} (${res.updated_paths.length})`);
        const next = { ...themeApplied };
        res.updated_paths.forEach((p) => { next[p] = true; });
        setThemeApplied(next);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setThemeApplying(false);
    }
  };

  const toggleSelection = (r, fk) => {
    if (r.readonly) return;
    const key = getApplyKey(r);
    setSelections((prev) => {
      const cur = { ...(prev[key] || {}) };
      cur[fk] = !cur[fk];
      return { ...prev, [key]: cur };
    });
  };

  const selectAllNonCompliant = () => {
    if (!scan) return;
    const pre = {};
    scan.resources.forEach((r) => {
      if (r.readonly) return;
      const f = {};
      FIELD_KEYS.forEach((fk) => {
        const fa = r.audit?.[fk];
        if (fa && !fa.compliant && fa.suggested) f[fk] = true;
      });
      if (Object.keys(f).length) pre[getApplyKey(r)] = f;
    });
    setSelections(pre);
  };

  // Iter 39 — Translate-mixed-languages auto-fix: like Auto-pilot but limited
  // to resources flagged by the [15] MIXED LANGUAGES rule. The LLM has already
  // produced rewrites in the store's primary language, we just push them.
  const runMixedLangAutoFix = async () => {
    if (!scan || !activeConn || applying) return;
    const candidates = (scan.resources || []).filter(hasMixedLang).filter((r) => !r.readonly);
    if (candidates.length === 0) {
      toast.info(t(lang, "shopify_mixed_lang_autofix_nothing"));
      return;
    }
    const items = [];
    let fieldCount = 0;
    candidates.forEach((r) => {
      const payload = {
        resource_type: r.resource_type,
        resource_id: parseInt(r.resource_id, 10),
      };
      let any = false;
      FIELD_KEYS.forEach((fk) => {
        const fa = r.audit?.[fk];
        if (fa && !fa.compliant && fa.suggested != null) {
          if (fk === "body_html") payload.body_html = fa.suggested;
          else if (fk === "seo_title") payload.seo_title = fa.suggested;
          else if (fk === "seo_description") payload.seo_description = fa.suggested;
          else payload.title = fa.suggested;
          any = true;
          fieldCount += 1;
        }
      });
      if (any) items.push(payload);
    });
    if (items.length === 0) {
      toast.info(t(lang, "shopify_mixed_lang_autofix_nothing"));
      return;
    }
    const msg = t(lang, "shopify_mixed_lang_autofix_confirm")
      .replace("{resources}", String(items.length))
      .replace("{fields}", String(fieldCount));
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setApplying(true);
    try {
      const res = await shopifyApply({ connection_id: activeConn, items });
      toast.success(`${t(lang, "shopify_mixed_lang_autofix_done")} — ${res.updated}/${res.total}`);
      if (res.failed > 0) {
        res.results.filter((r) => r.error).forEach((r) => {
          toast.error(`${r.resource_type}#${r.resource_id}: ${r.error}`);
        });
      }
      setSelections({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setApplying(false);
    }
  };

  const runAutoPilot = async () => {
    if (!scan || !activeConn || applying) return;
    // Collect non-compliant fields BUT only for resources whose score is at/below the threshold
    const items = [];
    let resourceCount = 0;
    let fieldCount = 0;
    let skippedHighScore = 0;
    scan.resources.forEach((r) => {
      if (r.readonly) return;
      const rscore = typeof r.score === "number" ? r.score : 100;
      if (rscore > scoreThreshold) { skippedHighScore += 1; return; }
      const payload = {
        resource_type: r.resource_type,
        resource_id: parseInt(r.resource_id, 10),
      };
      let any = false;
      FIELD_KEYS.forEach((fk) => {
        const fa = r.audit?.[fk];
        if (fa && !fa.compliant && fa.suggested != null) {
          if (fk === "body_html") payload.body_html = fa.suggested;
          else if (fk === "seo_title") payload.seo_title = fa.suggested;
          else if (fk === "seo_description") payload.seo_description = fa.suggested;
          else payload.title = fa.suggested;
          any = true;
          fieldCount += 1;
        }
      });
      if (any) {
        items.push(payload);
        resourceCount += 1;
      }
    });
    if (items.length === 0) {
      toast.info(t(lang, "shopify_autopilot_nothing"));
      return;
    }
    const msg = t(lang, "shopify_autopilot_confirm")
      .replace("{resources}", String(resourceCount))
      .replace("{fields}", String(fieldCount))
      .replace("{threshold}", String(scoreThreshold))
      .replace("{skipped}", String(skippedHighScore));
    // eslint-disable-next-line no-alert
    if (!window.confirm(msg)) return;
    setApplying(true);
    try {
      const res = await shopifyApply({ connection_id: activeConn, items });
      toast.success(`${t(lang, "shopify_autopilot_done")} — ${res.updated}/${res.total}`);
      if (res.failed > 0) {
        res.results.filter((r) => r.error).forEach((r) => {
          toast.error(`${r.resource_type}#${r.resource_id}: ${r.error}`);
        });
      }
      setSelections({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setApplying(false);
    }
  };

  const applySelected = async () => {
    if (!scan || !activeConn) return;
    const items = [];
    scan.resources.forEach((r) => {
      if (r.readonly) return;
      const sel = selections[getApplyKey(r)] || {};
      const payload = {
        resource_type: r.resource_type,
        resource_id: parseInt(r.resource_id, 10),
      };
      let any = false;
      FIELD_KEYS.forEach((fk) => {
        if (sel[fk] && r.audit?.[fk]?.suggested != null) {
          if (fk === "body_html") payload.body_html = r.audit[fk].suggested;
          else if (fk === "seo_title") payload.seo_title = r.audit[fk].suggested;
          else if (fk === "seo_description") payload.seo_description = r.audit[fk].suggested;
          else payload.title = r.audit[fk].suggested;
          any = true;
        }
      });
      if (any) items.push(payload);
    });
    if (items.length === 0) {
      toast.info("Nothing selected");
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(t(lang, "shopify_confirm_apply"))) return;
    setApplying(true);
    try {
      const res = await shopifyApply({ connection_id: activeConn, items });
      toast.success(`${t(lang, "shopify_applied")} — ${res.updated}/${res.total}`);
      if (res.failed > 0) {
        res.results.filter((r) => r.error).forEach((r) => {
          toast.error(`${r.resource_type}#${r.resource_id}: ${r.error}`);
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="shopify-panel">
      <ShopifyHealthBanner lang={lang} />
      {/* Connections */}
      <section className="border border-neutral-900 bg-white">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-900 bg-neutral-900 text-white">
          <ShoppingBag size={14} weight="fill" />
          <div className="text-xs uppercase tracking-[0.2em] font-mono">
            {t(lang, "shopify_title")}
          </div>
          <span className="ml-auto text-[10px] font-mono opacity-70">
            {conns.length}
          </span>
        </div>

        {conns.length === 0 && !loadingConns && (
          <div className="px-4 py-3 text-xs font-mono text-neutral-500" data-testid="shopify-no-conn">
            {t(lang, "shopify_no_conn")}
          </div>
        )}

        {conns.length > 0 && (
          <ul className="divide-y divide-neutral-200">
            {conns.map((c) => (
              <li
                key={c.id}
                className={`flex items-center gap-3 px-4 py-3 ma-trans ${
                  activeConn === c.id ? "bg-neutral-50" : ""
                }`}
                data-testid={`shopify-conn-${c.id}`}
              >
                <button
                  type="button"
                  onClick={() => { setActiveConn(c.id); setPickedProductIds([]); }}
                  className="flex-1 text-left min-w-0"
                  data-testid={`shopify-conn-select-${c.id}`}
                >
                  <div className="flex items-center gap-2 text-sm font-medium text-neutral-900">
                    <span
                      className={`inline-block w-2 h-2 ${
                        activeConn === c.id ? "bg-emerald-600" : "bg-neutral-300"
                      }`}
                    />
                    {c.name}
                  </div>
                  <div className="text-[11px] font-mono text-neutral-500 truncate">
                    {c.store_url}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => testConn(c.id)}
                  className="inline-flex items-center gap-1 border border-neutral-900 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest hover:bg-neutral-100 ma-trans"
                  data-testid={`shopify-conn-test-${c.id}`}
                >
                  <Pulse size={12} />
                  {t(lang, "shopify_test")}
                </button>
                <button
                  type="button"
                  onClick={() => removeConn(c.id)}
                  className="text-neutral-500 hover:text-red-700 p-1 ma-trans"
                  aria-label="delete"
                  data-testid={`shopify-conn-delete-${c.id}`}
                >
                  <Trash size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Add connection form */}
        <form
          onSubmit={addConnection}
          className="p-4 border-t border-neutral-200 space-y-3 bg-neutral-50"
          data-testid="shopify-add-form"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 flex items-center gap-1.5">
            <Plus size={12} /> {t(lang, "shopify_add_conn")}
          </div>

          {/* OAuth install block */}
          <HelpBlock
            lang={lang}
            titleKey="shopify_help_auto_title"
            steps={[
              "shopify_help_auto_step1",
              "shopify_help_auto_step2",
              "shopify_help_auto_step3",
              "shopify_help_auto_step4",
            ]}
            troubleshootKey="shopify_help_auto_troubleshoot"
            testid="shopify-help-auto"
          />
          <div className="flex items-center gap-2 p-3 border border-neutral-900 bg-white" data-testid="shopify-oauth-block">
            <input
              type="text"
              value={oauthShop}
              onChange={(e) => setOauthShop(e.target.value)}
              placeholder="mystore.myshopify.com"
              className="flex-1 border border-neutral-900 bg-white px-3 py-2 text-sm font-mono ma-focus"
              data-testid="shopify-oauth-shop-input"
              disabled={!oauthConfigured}
            />
            <button
              type="button"
              onClick={startOAuth}
              disabled={!oauthConfigured}
              className="inline-flex items-center gap-2 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2 text-xs uppercase tracking-widest font-medium ma-trans"
              data-testid="shopify-oauth-install-button"
              title={oauthConfigured ? "" : "Not configured. Set SHOPIFY_API_KEY/SECRET in backend/.env"}
            >
              <ShoppingBag size={12} weight="fill" />
              Install with Shopify
            </button>
          </div>
          {!oauthConfigured && (
            <p className="text-[10px] text-amber-700 font-mono uppercase tracking-widest">
              OAuth app not configured — use the manual connection below, or set SHOPIFY_API_KEY / SHOPIFY_API_SECRET in backend/.env.
            </p>
          )}

          <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500">
            — or manual Custom App —
          </div>
          <HelpBlock
            lang={lang}
            titleKey="shopify_help_manual_title"
            steps={[
              "shopify_help_manual_step1",
              "shopify_help_manual_step2",
              "shopify_help_manual_step3",
              "shopify_help_manual_step4",
              "shopify_help_manual_step5",
              "shopify_help_manual_step6",
              "shopify_help_manual_step7",
            ]}
            troubleshootKey="shopify_help_manual_troubleshoot"
            testid="shopify-help-manual"
          />
          <div
            className="flex items-start gap-2 border border-rose-700 bg-rose-50 px-3 py-2 text-[11px] font-mono text-rose-900"
            data-testid="shopify-token-warning"
          >
            <span className="text-sm leading-none pt-0.5">⚠</span>
            <div>
              <strong className="uppercase tracking-widest text-[10px]">
                {t(lang, "shopify_token_warning_title")}
              </strong>
              <p className="mt-1">{t(lang, "shopify_token_warning_body")}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t(lang, "shopify_name_ph")}
              className="border border-neutral-900 bg-white px-3 py-2 text-sm font-mono ma-focus"
              data-testid="shopify-name-input"
            />
            <input
              type="text"
              required
              value={form.store_url}
              onChange={(e) => setForm({ ...form, store_url: e.target.value })}
              placeholder={t(lang, "shopify_store_ph")}
              className="border border-neutral-900 bg-white px-3 py-2 text-sm font-mono ma-focus"
              data-testid="shopify-store-input"
            />
            <input
              type="password"
              required
              value={form.access_token}
              onChange={(e) => setForm({ ...form, access_token: e.target.value })}
              placeholder={t(lang, "shopify_token_ph")}
              className="border border-neutral-900 bg-white px-3 py-2 text-sm font-mono ma-focus"
              data-testid="shopify-token-input"
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] text-neutral-500 flex-1">
              {t(lang, "shopify_token_help")}
            </p>
            <button
              type="submit"
              disabled={connecting}
              className="inline-flex items-center gap-2 bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-widest font-medium ma-trans"
              data-testid="shopify-connect-button"
            >
              {connecting ? <CircleNotch size={12} className="animate-spin" /> : <Plus size={12} />}
              {t(lang, "shopify_connect")}
            </button>
          </div>
        </form>
      </section>

      {/* Scan controls */}
      {activeConn && (
        <section className="border border-neutral-900 bg-white" data-testid="shopify-scan-controls">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-neutral-900">
            <div className="text-xs uppercase tracking-[0.2em] font-mono">
              {t(lang, "shopify_scan")}
            </div>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
                {t(lang, "shopify_resources")}
              </div>
              <div className="flex flex-wrap gap-2">
                {RESOURCE_TYPES.map((r) => {
                  const selected = selectedResources.includes(r);
                  return (
                    <button
                      type="button"
                      key={r}
                      onClick={() => toggleResource(r)}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest border ma-trans ${
                        selected
                          ? "bg-neutral-900 text-white border-neutral-900"
                          : "bg-white text-neutral-900 border-neutral-900 hover:bg-neutral-100"
                      }`}
                      data-testid={`shopify-res-toggle-${r}`}
                    >
                      {selected ? <CheckSquare size={12} weight="fill" /> : <Square size={12} />}
                      {t(lang, `res_${r}`)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex items-end gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
                  {t(lang, "shopify_max_per")}
                </div>
                <Select value={String(maxPer)} onValueChange={(v) => setMaxPer(parseInt(v, 10))}>
                  <SelectTrigger
                    className="h-10 w-28 rounded-none border border-neutral-900 font-mono text-sm shadow-none"
                    data-testid="shopify-maxper-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border border-neutral-900 shadow-none">
                    {[5, 10, 20, 30, 50, 100, 200, 500].map((n) => (
                      <SelectItem key={n} value={String(n)} className="rounded-none font-mono">
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
                  {t(lang, "speed_label")}
                </div>
                <Select value={shopifySpeed} onValueChange={setShopifySpeed}>
                  <SelectTrigger
                    className="h-10 w-40 rounded-none border border-neutral-900 font-mono text-sm shadow-none"
                    data-testid="shopify-speed-select"
                    title={shopifySpeed === "fast" ? t(lang, "speed_fast_hint") : t(lang, "speed_accurate_hint")}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border border-neutral-900 shadow-none">
                    <SelectItem value="fast" className="rounded-none font-mono">{t(lang, "speed_fast")} · Haiku</SelectItem>
                    <SelectItem value="accurate" className="rounded-none font-mono">{t(lang, "speed_accurate")} · Sonnet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-500 mb-2">
                  {t(lang, "store_lang_label")}
                  {storeLangDetection?.detected && (
                    <span
                      className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-widest border ${
                        storeLangDetection.confidence === "high"
                          ? "border-emerald-600 text-emerald-700 bg-emerald-50"
                          : storeLangDetection.confidence === "medium"
                            ? "border-amber-600 text-amber-700 bg-amber-50"
                            : "border-neutral-400 text-neutral-600 bg-neutral-50"
                      }`}
                      data-testid="shopify-store-lang-detected"
                      title={`Source: ${storeLangDetection.source} · primary_locale=${storeLangDetection.primary_locale || "∅"} · products signal=${storeLangDetection.products_signal}`}
                    >
                      auto · {storeLangDetection.detected.toUpperCase()} ({storeLangDetection.confidence})
                    </span>
                  )}
                </div>
                <Select value={storeLang || "__auto__"} onValueChange={(v) => {
                  setStoreLangManuallyChanged(true);
                  setStoreLang(v === "__auto__" ? "" : v);
                }}>
                  <SelectTrigger
                    className="h-10 w-40 rounded-none border border-neutral-900 font-mono text-sm shadow-none"
                    data-testid="shopify-store-lang-select"
                    title={t(lang, "store_lang_hint")}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none border border-neutral-900 shadow-none">
                    <SelectItem value="__auto__" className="rounded-none font-mono">🌐 Auto (preserve)</SelectItem>
                    <SelectItem value="fr" className="rounded-none font-mono">🇫🇷 FR — Français</SelectItem>
                    <SelectItem value="en" className="rounded-none font-mono">🇬🇧 EN — English</SelectItem>
                    <SelectItem value="es" className="rounded-none font-mono">🇪🇸 ES — Español</SelectItem>
                    <SelectItem value="it" className="rounded-none font-mono">🇮🇹 IT — Italiano</SelectItem>
                    <SelectItem value="pt" className="rounded-none font-mono">🇵🇹 PT — Português</SelectItem>
                    <SelectItem value="de" className="rounded-none font-mono">🇩🇪 DE — Deutsch</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                disabled={scanning || !activeConn}
                className={`ml-auto inline-flex items-center gap-2 border ${
                  pickedProductIds.length
                    ? "border-emerald-700 bg-emerald-50 text-emerald-900 hover:bg-emerald-100"
                    : "border-neutral-900 bg-white text-neutral-900 hover:bg-neutral-100"
                } disabled:opacity-60 px-3 py-2.5 text-xs uppercase tracking-widest font-medium ma-trans`}
                data-testid="shopify-pick-products-button"
                title={t(lang, "product_picker_hint")}
              >
                <Package size={14} />
                {pickedProductIds.length
                  ? `${t(lang, "product_picker_picked_count")} (${pickedProductIds.length})`
                  : t(lang, "product_picker_button")}
              </button>
              {pickedProductIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setPickedProductIds([])}
                  disabled={scanning}
                  className="inline-flex items-center px-2 py-2.5 text-[10px] uppercase tracking-widest font-mono text-neutral-500 hover:text-rose-700 ma-trans"
                  data-testid="shopify-pick-products-clear"
                  title={t(lang, "product_picker_reset_hint")}
                >
                  ✕
                </button>
              )}
              <button
                type="button"
                onClick={runScan}
                disabled={scanning || selectedResources.length === 0}
                className="inline-flex items-center gap-2 bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-60 px-4 py-2.5 text-xs uppercase tracking-widest font-medium ma-trans"
                data-testid="shopify-scan-button"
              >
                {scanning ? <CircleNotch size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
                {scanning
                  ? (scanProgress && scanProgress.total > 0
                      ? `${t(lang, "shopify_scanning")} ${scanProgress.done}/${scanProgress.total}`
                      : t(lang, "shopify_scanning"))
                  : (pickedProductIds.length
                      ? `${t(lang, "shopify_scan")} (${pickedProductIds.length})`
                      : t(lang, "shopify_scan"))}
              </button>
              <button
                type="button"
                onClick={runThemeScan}
                disabled={themeScanning}
                className="inline-flex items-center gap-2 border border-neutral-900 bg-white text-neutral-900 hover:bg-neutral-900 hover:text-white disabled:opacity-60 px-4 py-2.5 text-xs uppercase tracking-widest font-medium ma-trans"
                data-testid="shopify-theme-scan-button"
                title={t(lang, "shopify_theme_scan_hint")}
              >
                {themeScanning ? <CircleNotch size={14} className="animate-spin" /> : <Palette size={14} />}
                {themeScanning ? t(lang, "shopify_theme_scanning") : t(lang, "shopify_theme_scan")}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Condition Audit (iter 30) */}
      {activeConn && (
        <ConditionAuditPanel
          activeConn={conns.find((c) => c.id === activeConn) || null}
        />
      )}

      {/* Scan results */}
      {scan && (() => {
        const allResources = scan.resources || [];
        const mixedLangCount = allResources.filter(hasMixedLang).length;
        const visibleResources = mixedLangOnly ? allResources.filter(hasMixedLang) : allResources;
        return (
        <section className="border border-neutral-900 bg-white" data-testid="shopify-scan-results">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-900 bg-neutral-900 text-white">
            <div className="text-xs uppercase tracking-[0.2em] font-mono">
              {t(lang, "shopify_scan_results")}
            </div>
            <div className="flex items-center gap-4 ml-auto text-[10px] font-mono uppercase tracking-widest">
              <span>
                {t(lang, "shopify_avg")}: <span className="font-bold">{scan.stats?.avg_score ?? 0}</span>
              </span>
              <span>
                {t(lang, "shopify_compliant")}: {scan.stats?.compliant ?? 0}/{scan.stats?.total ?? 0}
              </span>
              {mixedLangCount > 0 && (
                <span className="text-rose-300">
                  🌍 {t(lang, "shopify_mixed_lang_count")}: <span className="font-bold">{mixedLangCount}</span>
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-neutral-200 bg-neutral-50">
            <button
              type="button"
              onClick={selectAllNonCompliant}
              className="inline-flex items-center gap-1.5 border border-neutral-900 bg-white px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest hover:bg-neutral-100 ma-trans"
              data-testid="shopify-select-all"
            >
              <CheckSquare size={12} />
              {t(lang, "shopify_select_all")}
            </button>
            <button
              type="button"
              onClick={runAutoPilot}
              disabled={applying}
              className="inline-flex items-center gap-1.5 border border-purple-700 bg-purple-700 text-white px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest hover:bg-purple-800 disabled:opacity-60 ma-trans"
              data-testid="shopify-autopilot-button"
              title={t(lang, "shopify_autopilot_hint")}
            >
              {applying ? <CircleNotch size={12} className="animate-spin" /> : <Pulse size={12} weight="fill" />}
              {t(lang, "shopify_autopilot")} ≤ {scoreThreshold}
            </button>
            <Select value={String(scoreThreshold)} onValueChange={(v) => setScoreThreshold(parseInt(v, 10))}>
              <SelectTrigger className="h-7 w-[90px] rounded-none border border-neutral-900 font-mono text-[10px] uppercase tracking-widest shadow-none" data-testid="shopify-autopilot-threshold">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none border border-neutral-900 shadow-none">
                {[40, 50, 60, 70, 80, 90, 100].map((n) => (
                  <SelectItem key={n} value={String(n)} className="rounded-none font-mono text-[10px]">
                    ≤ {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => setMixedLangOnly((v) => !v)}
              disabled={mixedLangCount === 0}
              className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans ${
                mixedLangOnly
                  ? "border-rose-700 bg-rose-700 text-white hover:bg-rose-800"
                  : "border-rose-700 bg-white text-rose-800 hover:bg-rose-50"
              } disabled:opacity-40 disabled:cursor-not-allowed`}
              data-testid="shopify-mixed-lang-filter"
              title={t(lang, "shopify_mixed_lang_filter_hint")}
            >
              🌍 {mixedLangOnly
                ? `${t(lang, "shopify_mixed_lang_filter_on")} (${mixedLangCount})`
                : `${t(lang, "shopify_mixed_lang_filter_off")} (${mixedLangCount})`}
            </button>
            <button
              type="button"
              onClick={runMixedLangAutoFix}
              disabled={mixedLangCount === 0 || applying}
              className="inline-flex items-center gap-1.5 border border-rose-800 bg-rose-800 text-white px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest hover:bg-rose-900 disabled:opacity-40 disabled:cursor-not-allowed ma-trans"
              data-testid="shopify-mixed-lang-autofix"
              title={t(lang, "shopify_mixed_lang_autofix_hint")}
            >
              {applying ? <CircleNotch size={12} className="animate-spin" /> : "🌍"}
              {t(lang, "shopify_mixed_lang_autofix_button")} ({mixedLangCount})
            </button>
            <button
              type="button"
              onClick={applySelected}
              disabled={applying}
              className="ml-auto inline-flex items-center gap-2 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60 px-4 py-1.5 text-[10px] font-mono uppercase tracking-widest ma-trans"
              data-testid="shopify-apply-button"
            >
              {applying ? <CircleNotch size={12} className="animate-spin" /> : <CloudArrowUp size={12} />}
              {applying ? t(lang, "shopify_applying") : t(lang, "shopify_apply_selected")}
            </button>
          </div>

          <div>
            {visibleResources.map((r) => (
              <ResourceRow
                key={`${r.resource_type}:${r.resource_id}`}
                r={r}
                lang={lang}
                selections={selections[getApplyKey(r)] || {}}
                onToggle={(fk) => toggleSelection(r, fk)}
              />
            ))}
            {visibleResources.length === 0 && (
              <div className="p-6 text-center text-xs font-mono text-neutral-500" data-testid="shopify-no-visible-resources">
                {mixedLangOnly
                  ? t(lang, "shopify_mixed_lang_filter_empty")
                  : "—"}
              </div>
            )}
          </div>
        </section>
        );
      })()}

      {/* Theme scan results */}
      {themeScan && (
        <section className="border border-neutral-900 bg-white" data-testid="shopify-theme-scan-results">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-900 bg-neutral-900 text-white">
            <Palette size={14} weight="fill" />
            <div className="text-xs uppercase tracking-[0.2em] font-mono">
              {t(lang, "shopify_theme_scan_results")}
            </div>
            <div className="flex items-center gap-4 ml-auto text-[10px] font-mono uppercase tracking-widest">
              <span>{themeScan.theme_name}</span>
              <span>{t(lang, "shopify_theme_strings_scanned")}: <span className="font-bold">{themeScan.scanned_strings}</span></span>
              <span>{t(lang, "shopify_theme_findings")}: <span className="font-bold" data-testid="shopify-theme-findings-count">{themeScan.findings?.length || 0}</span></span>
              {(themeScan.findings || []).length > 0 && (
                <>
                  <Select value={themeSeverity} onValueChange={setThemeSeverity}>
                    <SelectTrigger className="h-7 w-[120px] rounded-none border border-white/30 bg-transparent text-white font-mono text-[10px] uppercase tracking-widest shadow-none" data-testid="shopify-theme-severity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none border border-neutral-900 shadow-none">
                      <SelectItem value="critical" className="rounded-none font-mono text-[10px]">≥ Critical</SelectItem>
                      <SelectItem value="warning" className="rounded-none font-mono text-[10px]">≥ Warning</SelectItem>
                      <SelectItem value="info" className="rounded-none font-mono text-[10px]">All severities</SelectItem>
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    onClick={runThemeAutoPilot}
                    disabled={themeApplying}
                    className="inline-flex items-center gap-1.5 border border-purple-400 bg-purple-700 text-white hover:bg-purple-800 disabled:opacity-60 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans"
                    data-testid="shopify-theme-autopilot-button"
                    title={t(lang, "shopify_theme_autopilot_hint")}
                  >
                    {themeApplying
                      ? <CircleNotch size={11} className="animate-spin" />
                      : <Pulse size={11} weight="fill" />}
                    {t(lang, "shopify_theme_autopilot")}
                  </button>
                  <button
                    type="button"
                    onClick={() => applyThemeFindings(themeScan.findings)}
                    disabled={themeApplying}
                    className="inline-flex items-center gap-1.5 border border-emerald-400 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans"
                    data-testid="shopify-theme-apply-all-button"
                  >
                    {themeApplying
                      ? <CircleNotch size={11} className="animate-spin" />
                      : <CloudArrowUp size={11} />}
                    {t(lang, "shopify_theme_apply_all")}
                  </button>
                </>
              )}
            </div>
          </div>

          {themeScan.summary && (
            <div className="px-4 py-2 bg-neutral-50 border-b border-neutral-200 text-[11px] font-mono text-neutral-700">
              {themeScan.summary}
            </div>
          )}

          <div>
            {(themeScan.findings || []).length === 0 ? (
              <div className="p-6 text-center" data-testid="shopify-theme-no-findings">
                <div className="inline-flex items-center gap-2 text-emerald-700 font-mono text-xs uppercase tracking-widest">
                  <ShoppingBag size={12} weight="fill" />
                  {t(lang, "shopify_theme_no_findings")}
                </div>
              </div>
            ) : (
              themeScan.findings.map((f, i) => (
                <div
                  key={i}
                  className="border-t border-neutral-200 px-4 py-3"
                  data-testid={`shopify-theme-finding-${i}`}
                >
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest ${
                      f.severity === "critical" ? "sev-critical" :
                      f.severity === "warning" ? "sev-warning" : "sev-info"
                    }`}>
                      <Warning size={10} weight="fill" />
                      {t(lang, `severity_${f.severity}`)}
                    </span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                      {f.category}
                    </span>
                    {f.section_id && (
                      <span className="text-[10px] font-mono text-neutral-400">
                        › {f.section_id}{f.setting_key ? ` › ${f.setting_key}` : ""}
                      </span>
                    )}
                    {f.rule && (
                      <span className="text-[10px] font-mono text-neutral-700 ml-auto truncate max-w-[60%]" title={f.rule}>
                        {f.rule}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 mb-2">
                    <div className="border border-red-300 bg-red-50">
                      <div className="px-2 py-1 border-b border-red-300 text-[10px] font-mono uppercase tracking-widest text-red-800">
                        {t(lang, "original")}
                      </div>
                      <pre className="p-2 text-[11px] font-mono whitespace-pre-wrap text-red-900">{f.original_text}</pre>
                    </div>
                    <div className="border border-emerald-400 bg-emerald-50">
                      <div className="px-2 py-1 border-b border-emerald-400 text-[10px] font-mono uppercase tracking-widest text-emerald-800">
                        {t(lang, "suggested")}
                      </div>
                      <pre className="p-2 text-[11px] font-mono whitespace-pre-wrap text-emerald-900">{f.suggested_text}</pre>
                    </div>
                  </div>
                  {f.explanation && (
                    <p className="text-[11px] text-neutral-700 leading-relaxed">
                      {f.explanation}
                    </p>
                  )}
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <div className="text-[10px] font-mono text-neutral-400 truncate flex-1" title={f.path}>
                      {f.path}
                    </div>
                    {themeApplied[f.path] ? (
                      <span
                        className="inline-flex items-center gap-1 border border-emerald-400 bg-emerald-50 text-emerald-800 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest"
                        data-testid={`shopify-theme-finding-applied-${i}`}
                      >
                        ✓ {t(lang, "shopify_theme_applied")}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => applyThemeFindings([f])}
                        disabled={themeApplying}
                        className="inline-flex items-center gap-1.5 border border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60 px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest ma-trans"
                        data-testid={`shopify-theme-finding-apply-${i}`}
                      >
                        <CloudArrowUp size={11} />
                        {t(lang, "shopify_theme_apply_one")}
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {(themeScan.findings || []).length > 0 && (
            <div className="border-t border-neutral-200 bg-amber-50 px-4 py-3 text-[11px] text-amber-900">
              <strong className="font-mono uppercase tracking-widest text-[10px]">{t(lang, "shopify_theme_apply_warn_title")}</strong>{" "}
              {t(lang, "shopify_theme_apply_warn_hint")}
            </div>
          )}
        </section>
      )}

      {/* Apply log (audit trail) */}
      <ApplyLogPanel lang={lang} connectionId={activeConn} />

      <ProductPickerModal
        open={pickerOpen}
        connId={activeConn}
        lang={lang}
        onClose={() => setPickerOpen(false)}
        onConfirm={(ids) => {
          setPickedProductIds(ids);
          setPickerOpen(false);
          if (ids.length === 0) {
            toast.info(t(lang, "product_picker_reset_toast"));
          } else {
            toast.success(`${t(lang, "product_picker_picked_toast")} · ${ids.length}`);
          }
        }}
      />
    </div>
  );
}

function ResourceRow({ r, lang, selections, onToggle }) {
  const [open, setOpen] = useState(false);
  const sc = scoreColor(r.compliance_score);
  return (
    <div className="border-t border-neutral-200" data-testid={`shopify-row-${r.resource_type}-${r.resource_id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-neutral-50 ma-trans"
        data-testid={`shopify-row-toggle-${r.resource_type}-${r.resource_id}`}
      >
        <span className={`inline-flex items-center justify-center w-12 h-7 text-xs font-mono border ${sc}`}>
          {r.compliance_score}
        </span>
        <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 w-36 shrink-0">
          {t(lang, `res_${r.resource_type}`)}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-neutral-900 truncate">
            {r.title_original || "—"}
          </div>
          {r.handle && (
            <div className="text-[10px] font-mono text-neutral-500 truncate">/{r.handle}</div>
          )}
        </div>
        {r.readonly && (
          <span className="text-[10px] font-mono uppercase tracking-widest border border-amber-500 bg-amber-50 text-amber-700 px-2 py-0.5">
            read-only
          </span>
        )}
        <CaretDown
          size={14}
          className={`text-neutral-500 ma-trans ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="px-4 pb-5 pt-1 space-y-3 ma-in">
          {r.summary && (
            <div className="border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-800">
              {r.summary}
            </div>
          )}
          {r.readonly && (
            <div className="border border-amber-500 bg-amber-50 p-3 text-xs font-mono text-amber-800">
              {t(lang, "shopify_readonly_note")}
            </div>
          )}
          {FIELD_KEYS.map((fk) => {
            const fa = r.audit?.[fk];
            if (!fa) return null;
            const original =
              fk === "title" ? r.title_original
              : fk === "body_html" ? r.body_html_original
              : fk === "seo_title" ? r.seo_title_original
              : r.seo_description_original;
            const hasRewrite = !fa.compliant && fa.suggested;
            return (
              <FieldDiff
                key={fk}
                lang={lang}
                fieldKey={fk}
                compliant={fa.compliant}
                issues={fa.issues || []}
                original={original || ""}
                suggested={fa.suggested}
                selected={!!selections[fk]}
                onToggle={() => onToggle(fk)}
                readonly={r.readonly}
                canApply={hasRewrite}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function FieldDiff({ lang, fieldKey, compliant, issues, original, suggested, selected, onToggle, readonly, canApply }) {
  return (
    <div
      className={`border ${compliant ? "border-neutral-200 bg-white" : "border-amber-400 bg-amber-50/30"}`}
      data-testid={`shopify-field-${fieldKey}`}
    >
      <div className="flex items-center gap-3 px-3 py-2 border-b border-neutral-200">
        <span className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 min-w-[140px]">
          {t(lang, FIELD_LABEL[fieldKey])}
        </span>
        {compliant ? (
          <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">
            {t(lang, "shopify_no_change")}
          </span>
        ) : (
          <span className="text-[10px] font-mono uppercase tracking-widest text-amber-800">
            {(issues || [])[0] || "issue"}
          </span>
        )}
        {canApply && !readonly && (
          <label className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest cursor-pointer select-none">
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggle}
              className="accent-neutral-900"
              data-testid={`shopify-select-${fieldKey}`}
            />
            apply
          </label>
        )}
      </div>
      {canApply ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          <pre className="p-3 text-xs font-mono whitespace-pre-wrap diff-removed border-r border-neutral-200 max-h-60 overflow-auto">
            {original || "—"}
          </pre>
          <pre className="p-3 text-xs font-mono whitespace-pre-wrap diff-added max-h-60 overflow-auto">
            {suggested || "—"}
          </pre>
        </div>
      ) : (
        <pre className="p-3 text-xs font-mono whitespace-pre-wrap text-neutral-700 max-h-40 overflow-auto">
          {original || "—"}
        </pre>
      )}
      {issues && issues.length > 1 && (
        <ul className="px-3 py-2 border-t border-neutral-200 text-[11px] text-neutral-600 list-disc ml-5">
          {issues.slice(1).map((i, idx) => (
            <li key={idx}>{i}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
